<template>
    <div :class="`number number-align-${labelAlign}`">
        <!-- Normal Mode -->
        <template v-if="!isSpecial">
            <label for="number" class="field-label">
                {{ fieldLabel }}
                <span v-if="isRequired" class="required-indicator">*</span>
            </label>
            <div class="input-container">
                <div :class="['input-wrapper', { 'error': showError && isRequired && isEmpty, 'filled': !isEmpty }]">
                    <input 
                        type="number" 
                        id="number" 
                        name="number" 
                        v-model="numberValue" 
                        @input="emitValues"
                        @focus="onFocus"
                        @blur="onBlur"
                        :class="{ 'has-value': !isEmpty }"
                        placeholder="Bir sayı girin"
                    />
                    <div class="input-border"></div>
                </div>
                <label for="number" :class="['input-label', { 'error': showError && isRequired && isEmpty }]">{{ subLabel }}</label>
                <div v-if="showError && isRequired && isEmpty" class="error-message">
                    {{ fieldLabel }} alanı zorunludur
                </div>
        </div>
        </template>
        
        <!-- Special Mode (Unchanged) -->
        <div v-if="isSpecial" class="special-input-wrapper">
            <input
                type="number"
                id="number-special"
                name="number"
                v-model="numberValue"
                @input="emitValues"
                class="special-input"
                :placeholder="fieldLabel"
            />
        </div>
    </div>
</template>

<script>
export default {
    name: "FormNumber",
    props: {
        fieldLabel: {
            type: String,
            required: true,
        },
        labelAlign: {
            type: String,
            required: true,
        },
        subLabel: {
            type: String,
            default: '',
        },
        isRequired: {
            type: Boolean,
            default: false,
        },
        showError: {
            type: Boolean,
            default: false,
        },
        value: {
            type: [Object, String],
            default: () => ({ number: 0 }),
        },
        formId: {
            type: String,
            required: true,
        },
        order: {
            type: Number,
            required: true,
        }
    },
    data() {
        const safeValue = (typeof this.value === 'object' && this.value !== null) ? this.value : {};
        return {
            numberValue: safeValue.number !== undefined ? safeValue.number : '', // Boş string başlasın
            isSpecial: false,
        };
    },
    computed: {
        isEmpty() {
            // Boş string, null, undefined veya sadece whitespace ise boş
            return this.numberValue === '' || 
                   this.numberValue === null || 
                   this.numberValue === undefined ||
                   (typeof this.numberValue === 'string' && this.numberValue.trim() === '');
        }
    },
    watch: {
        value: {
            handler(newValue) {
                const safeValue = (typeof newValue === 'object' && newValue !== null) ? newValue : {};
                this.numberValue = safeValue.number !== undefined ? safeValue.number : '';
            },
            deep: true,
        },
        showError: {
            handler() {
                // showError false olduğunda değeri sıfırlama
                // Kullanıcının girdiği değer korunsun
            },
        },
    },
    methods: {
        emitValues() {
            // Number değerini düzgün şekilde parse et
            const numericValue = this.isEmpty ? null : parseFloat(this.numberValue);
            
            const numberData = {
                number: numericValue,
                fieldLabel: this.fieldLabel,
                subLabel: this.subLabel,
            };
            
            this.$emit("input", numberData);
            this.$emit("validate", {
                fieldId: this._uid,
                hasError: this.isRequired && this.isEmpty,
            });
            
            // LocalStorage'a kaydet
            localStorage.setItem(`${this.formId}-${this.order}`, JSON.stringify(numberData));
            
            console.log('🔢 Number field emitted:', {
                fieldId: this.formId,
                rawValue: this.numberValue,
                numericValue: numericValue,
                isEmpty: this.isEmpty,
                isRequired: this.isRequired,
                hasError: this.isRequired && this.isEmpty
            });
        },
        loadValues() {
            const savedData = localStorage.getItem(`${this.formId}-${this.order}`);
            if (savedData) {
                try {
                    const parsedData = JSON.parse(savedData);
                    this.numberValue = parsedData.number !== undefined && parsedData.number !== null ? parsedData.number : '';
                    
                    console.log('🔢 Number values loaded from localStorage:', {
                        fieldId: this.formId,
                        savedValue: parsedData.number,
                        currentValue: this.numberValue
                    });
                } catch (error) {
                    console.error('❌ Error parsing saved number data:', error);
                }
            }
        },
        onFocus(event) {
            if (!this.isSpecial) {
                event.target.parentElement.classList.add('focused');
            }
        },
        onBlur(event) {
            if (!this.isSpecial) {
                event.target.parentElement.classList.remove('focused');
            }
        }
    },
    mounted() {
        const special = this.$route.query.special;
        if (special === "true") {
            this.isSpecial = true;
        }
        
        // Slug değeri varsa onu kullan, yoksa localStorage'dan yükle
        const hasSlugValue = this.value && typeof this.value === 'object' && this.value.number !== undefined;
        
        if (!hasSlugValue) {
            this.loadValues();
        }
        
        // İlk emit'i yap
        this.emitValues();
        
        console.log('🔢 Number field mounted:', {
            fieldId: this.formId,
            hasSlugValue: hasSlugValue,
            initialValue: this.numberValue,
            isEmpty: this.isEmpty
        });
    }
};
</script>

<style lang="scss" scoped>
/* Special Mode Styles (Unchanged) */
.special-input-wrapper {
    position: relative;
    width: 100%;
    max-width: 400px;
    margin: 0 auto;
    padding-left: 18px;
}

.special-input {
    width: 100%;
    padding: 12px;
    font-size: 18px;
    border: none;
    border-radius: 20px;
    background: linear-gradient(90deg, #e2e4e4, #f7f7f7);
    color: #000;
    text-align: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
}

.special-input:focus {
    outline: none;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    color: black;
    font-weight: bold;
    font-size: 14px;
}

.special-input::placeholder {
    color: black;
    font-weight: bold;
    font-size: 14px;
}

/* Base Number Styles (Normal Mode) */
.number {
    width: 100%;
    max-width: 700px;
    margin: clamp(0.5rem, 1.5vw, 1rem) auto;
    padding: clamp(0.625rem, 1.5vw, 0.875rem);
    position: relative;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    
    background: linear-gradient(145deg, 
        rgba(255, 255, 255, 0.02) 0%,
        rgba(255, 255, 255, 0.01) 100%);
    border-radius: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.06);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
        background: linear-gradient(145deg, 
            rgba(255, 255, 255, 0.03) 0%,
            rgba(255, 255, 255, 0.02) 100%);
    }

    .field-label {
        display: block;
        font-size: clamp(0.95rem, 2.2vw, 1.1rem);
        font-weight: 600;
        margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        color: var(--text-primary, rgba(55, 65, 81, 0.9));
        line-height: 1.4;
        letter-spacing: -0.01em;
        
        .required-indicator {
            color: #ef4444;
            margin-left: 0.125rem;
            font-weight: 700;
        }
    }

    .input-container {
        position: relative;
        min-width: 0; // Flex item için minimum genişlik
    }

    .input-wrapper {
        position: relative;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        input {
            width: 100%;
            padding: clamp(0.6rem, 1.5vw, 0.75rem) clamp(0.6rem, 1.2vw, 0.75rem);
            font-size: clamp(0.875rem, 1.8vw, 0.9rem);
            line-height: 1.4;
            color: var(--text-primary, rgba(55, 65, 81, 0.9));
            background: rgba(255, 255, 255, 0.8);
            border: 1px solid rgba(209, 213, 219, 0.6);
            border-radius: 0.375rem;
            box-sizing: border-box;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            font-family: inherit;
            backdrop-filter: blur(10px);
            
            &::placeholder {
                color: rgba(107, 114, 128, 0.6);
            }
            
            &:focus {
                outline: none;
                border-color: var(--primary, #6366f1);
                background: rgba(255, 255, 255, 0.95);
                box-shadow: 
                    0 0 0 3px rgba(99, 102, 241, 0.1),
                    0 4px 12px rgba(0, 0, 0, 0.05);
                transform: translateY(-1px);
            }
            
            &:hover:not(:focus) {
                border-color: rgba(107, 114, 128, 0.4);
                background: rgba(255, 255, 255, 0.85);
            }

            /* Number input with native spin buttons */
            &[type="number"] {
                padding-right: 0.5rem;
            }
        }

        .input-border {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 2px;
            border-radius: 1px;
            transform: scaleX(0);
            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        &.focused .input-border,
        input:focus ~ .input-border {
            transform: scaleX(1);
        }

        &.error {
            input {
                border-color: #ef4444;
                background: rgba(254, 242, 242, 0.8);
                
                &:focus {
                    border-color: #ef4444;
                    box-shadow: 
                        0 0 0 3px rgba(239, 68, 68, 0.1),
                        0 4px 12px rgba(239, 68, 68, 0.1);
                }
            }
            
            .input-border {
                background: #ef4444;
            }
        }
    }

    .input-label {
        display: block;
        font-size: clamp(0.75rem, 1.6vw, 0.8rem);
        color: rgba(107, 114, 128, 0.8);
        margin-top: clamp(0.25rem, 0.8vw, 0.375rem);
        font-weight: 500;
        transition: color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        
        &.error {
            color: #ef4444;
        }
    }

    .error-message {
        color: #ef4444;
        font-size: clamp(0.8rem, 1.8vw, 0.875rem);
        margin-top: clamp(0.375rem, 1vw, 0.5rem);
        font-weight: 500;
        display: flex;
        align-items: center;
        gap: 0.375rem;
        
        &::before {
            content: '⚠';
            font-size: 0.875em;
        }
    }
}

/* Left Alignment - Label solda, input sağda */
.number-align-left {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);
    text-align: left;

    .field-label {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.6rem, 1.5vw, 0.75rem);
        text-align: left;
    }

    .input-container {
        flex: 1;
    }
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;
        
        .field-label {
            min-width: auto;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
    }

        .input-container {
        width: 100%;
        }
    }
}

/* Right Alignment - Label solda sağa yaslanmış, input sağda */
.number-align-right {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    .field-label {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.6rem, 1.5vw, 0.75rem);
        text-align: right;
    }

    .input-container {
        flex: 1;
    }
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;
        
        .field-label {
            min-width: auto;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
            text-align: left;
    }

        .input-container {
        width: 100%;
    }
}
}

/* Center/Top Alignment (Default) */
.number-align-top {
    text-align: left;
    flex-direction: column;
}

// Mobil optimizasyonları
@media (max-width: 768px) {
    .number {
        margin: clamp(0.25rem, 1vw, 0.5rem) auto;
        padding: clamp(0.5rem, 1.2vw, 0.75rem);
        
        .field-label {
            font-size: clamp(0.9rem, 2vw, 1rem);
            margin-bottom: clamp(0.375rem, 1vw, 0.5rem);
    }

    .input-wrapper {
            input {
                padding: clamp(0.75rem, 2vw, 0.875rem) clamp(0.625rem, 1.5vw, 0.75rem);
                font-size: clamp(0.875rem, 2vw, 0.9rem);
                border-radius: 0.5rem;
                
                &[type="number"] {
                    padding-right: clamp(0.5rem, 1vw, 0.625rem);
                }
            }
            
            .input-label {
                font-size: clamp(0.75rem, 1.8vw, 0.8rem);
                margin-top: clamp(0.25rem, 0.8vw, 0.375rem);
        }
        }
        
        .error-message {
            font-size: clamp(0.8rem, 1.8vw, 0.85rem);
            margin-top: clamp(0.25rem, 0.8vw, 0.375rem);
        }
    }
}

@media (max-width: 480px) {
    .number {
        margin: 0.25rem auto;
        padding: 0.5rem;
        
        .input-wrapper {
            input {
                padding: 0.75rem 0.625rem;
                font-size: 0.875rem;
                
                &[type="number"] {
                    padding-right: 0.5rem;
                }
            }
            
            .input-label {
                font-size: 0.75rem;
                margin-top: 0.25rem;
            }
        }

        .error-message {
            font-size: 0.8rem;
            margin-top: 0.25rem;
        }
    }
}

/* Dark mode devre dışı - Her zaman açık tema kullan */

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
    .number,
    .input-wrapper,
    .input-wrapper input,
    .input-label,
    .input-border {
        transition: none;
    }
    
    .input-wrapper input:focus {
        transform: none;
    }
}

/* High Performance Mode */
.input-wrapper {
    will-change: transform;
    
    input {
        will-change: transform, box-shadow;
    }
    
    .input-border {
        will-change: transform;
        }
    }

.input-label {
    will-change: color;
}
</style>